import { useState, useEffect } from 'react'
import { usePermission } from '@fs/zion-permissions'
import { workflowStatuses } from '../../../../helpers'

const METIS = {
  REQUEST: 'RequestCameraRework',
  REWORK: 'CameraRework',
}

const VALID_REQUEST_STATUS = [
  workflowStatuses.COMPLETED,
  workflowStatuses.FAILURE,
  workflowStatuses.ERROR,
  workflowStatuses.CANCELLED,
]
const VALID_PROGRESS_STATUS = [workflowStatuses.AVAILABLE, workflowStatuses.IN_PROGRESS]

const REWORK_STATUS = {
  approve: 'approve',
  request: 'request',
  cancel: 'cancel',
}

/**
 *
 * @param {action, state} lastItem the last item in the rework status history of an image group
 * @returns currentReworkStatus: the current action available to update the rework status of an image group
 *          resetCurrentReworkStatus(action): reset the currentReworkStatus based on the last **action** that was applied
 *          setReworkStatusError(): run this function when an error occurs while working with the reworkStatus
 *          REWORK_STATUS: the status' available for rework
 */
export default function useCurrentReworkStatus(lastItem) {
  const [currentReworkStatus, setCurrentReworkStatus] = useState('')
  const [authorized] = usePermission('LightyearRequestReworkPermission', 'LightyearContext')
  const [canApprove] = usePermission('LightyearApproveReworkPermission', 'LightyearContext')

  function resetCurrentReworkStatus(action) {
    if (action === REWORK_STATUS.approve) setCurrentReworkStatus(REWORK_STATUS.cancel)
    else if (action === REWORK_STATUS.cancel) setCurrentReworkStatus(REWORK_STATUS.request)
    else if (action === REWORK_STATUS.request && !canApprove) setCurrentReworkStatus('')
    else setCurrentReworkStatus(REWORK_STATUS.request)
  }

  useEffect(() => {
    const lastAction = lastItem?.action
    const lastStatus = lastItem?.state

    // Only proceed if we have any relevant state to process
    const shouldProcessReworkStatus = lastAction || lastStatus || authorized
    if (!shouldProcessReworkStatus) return

    const determineReworkStatus = () => {
      const approve = { request: false, cancel: false, approval: false }

      // Check if user can request
      const isAuthorizedWithoutAction = authorized && !lastAction
      const isValidRequestStatus = lastStatus && VALID_REQUEST_STATUS.includes(lastStatus)
      const isRequestOrReworkAction = lastAction === METIS.REQUEST || lastAction === METIS.REWORK
      approve.request = isAuthorizedWithoutAction || (isRequestOrReworkAction && isValidRequestStatus)

      // Check if user can approve
      approve.approval = canApprove && lastAction === METIS.REQUEST && lastStatus === workflowStatuses.AVAILABLE

      // Check if user can cancel
      const canCancelRequest = authorized && lastAction === METIS.REQUEST && VALID_PROGRESS_STATUS.includes(lastStatus)
      const canCancelReworkInProgress = lastAction === METIS.REWORK && lastStatus === workflowStatuses.IN_PROGRESS
      const canCancelReworkAvailable =
        lastAction === METIS.REWORK && canApprove && lastStatus === workflowStatuses.AVAILABLE
      approve.cancel = canCancelRequest || canCancelReworkInProgress || canCancelReworkAvailable

      return approve
    }

    const getStatusFromApprovals = (approve) => {
      if (approve.approval) return REWORK_STATUS.approve
      if (approve.request) return lastAction ? '' : REWORK_STATUS.request
      if (approve.cancel) return REWORK_STATUS.cancel
      return ''
    }

    const approvals = determineReworkStatus()
    const newStatus = getStatusFromApprovals(approvals)
    setCurrentReworkStatus(newStatus)
  }, [lastItem, authorized, canApprove])

  return {
    currentReworkStatus,
    resetCurrentReworkStatus,
    setReworkStatusError: () => setCurrentReworkStatus(''),
    REWORK_STATUS,
  }
}
